import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";
import { useDebounce } from "@uidotdev/usehooks";

import { useResourcesQuery, ResourceDocument, type ResourceQuery } from "@graphql/resources/resources.g";
import { InfiniteSelectBase, InfiniteSelectDataHook, SelectOption } from "@components/Selects/InfiniteSelectBase";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE = 300;

// Hook to provide resources data for the infinite select
const useInfiniteResources = (
  searchQuery: string,
  excludeId: string | undefined,
  isOpen: boolean
): InfiniteSelectDataHook => {
  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE);

  const query = useInfiniteQuery({
    queryKey: ["InfiniteResources", debouncedSearchQuery, excludeId],
    queryFn: async ({ pageParam }) => {
      const result = await useResourcesQuery.fetcher({
        first: PAGE_SIZE,
        offset: pageParam as number,
        isPrimaryResource: true,
        orderBy: "productName",
        ...(debouncedSearchQuery && {
          productName_Icontains: debouncedSearchQuery,
        }),
      })();
      return result;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = allPages.length * PAGE_SIZE;
      const totalCount = lastPage?.resources?.totalCount || 0;

      if (currentOffset < totalCount) {
        return currentOffset;
      }
      return undefined;
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const options = useMemo(() => {
    const allOptions: Array<{ id: string; label: string }> = [];

    if (query.data?.pages) {
      query.data.pages.forEach((page) => {
        const resources = page?.resources?.edges || [];
        resources.forEach((edge) => {
          const node = edge?.node;
          if (node && node.id !== excludeId) {
            allOptions.push({
              id: node.id,
              label: `${node.productName || ""} (${node.vendorName || ""})`,
            });
          }
        });
      });
    }

    return allOptions;
  }, [query.data, excludeId]);

  return {
    ...query,
    options,
  };
};

type Props = {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  excludeId?: string;
  placeholder?: string;
};

export const InfiniteResourceSelect = ({
  value,
  onChange,
  excludeId,
  placeholder = "Search and select resource...",
}: Props) => {
  const fetchSelectedOption = useCallback(async (id: string): Promise<SelectOption | null> => {
    try {
      const response = await fetch(import.meta.env.VITE_API_URL_GRAPHQL as string, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: ResourceDocument,
          variables: { id },
        }),
      });

      const json = await response.json();

      if (json.errors) {
        console.error("GraphQL error fetching resource:", json.errors);
        return null;
      }

      const data = json.data as ResourceQuery;
      const resource = data?.resource;

      if (resource) {
        return {
          id: resource.id,
          label: `${resource.productName || ""} (${resource.vendorName || ""})`,
        };
      }
    } catch (error) {
      console.error("Failed to fetch selected resource:", error);
    }
    return null;
  }, []);

  // Create a wrapper hook that includes excludeId in closure
  const useDataWithExclude = useMemo(() => {
    return (searchQuery: string, isOpen: boolean) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useInfiniteResources(searchQuery, excludeId, isOpen);
    };
  }, [excludeId]);

  return (
    <InfiniteSelectBase
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      useData={useDataWithExclude}
      fetchSelectedOption={fetchSelectedOption}
      loadingText="Loading resources..."
      noResultsText="No resources found"
      loadingMoreText="Loading more..."
    />
  );
};